export type MotionState = 'fixation' | 'pursuit' | 'saccade';

export interface GazeEnhancementParams {
  minDtSec: number;
  maxDtSec: number;
  qPos: number;
  qVelFixation: number;
  qVelPursuit: number;
  qVelSaccade: number;
  rFixation: number;
  rPursuit: number;
  rSaccade: number;
  fixationEnterSpeed: number;
  fixationExitSpeed: number;
  saccadeEnterSpeed: number;
  saccadeExitSpeed: number;
  saccadeEnterAccel: number;
  saccadeExitAccel: number;
  minStateHoldMs: number;
  tauFixationSec: number;
  tauPursuitSec: number;
  tauSaccadeSec: number;
  maxLeadNorm: number;
  processNoiseXScale: number;
  processNoiseYScale: number;
  measurementNoiseXScale: number;
  measurementNoiseYScale: number;
  lagGainX: number;
  lagGainY: number;
}

export interface GazeEnhancementToggles {
  enableKalman: boolean;
  enableAxisAsymmetry: boolean;
  enableMotionState: boolean;
  enableLagCompensation: boolean;
  enableDtClamp: boolean;
}

export interface GazeEnhancementInput {
  rawNormX: number;
  rawNormY: number;
  timestampMs: number;
  gazeOpen: boolean;
}

export interface GazeEnhancementOutput {
  rawNorm: [number, number];
  kalmanNorm: [number, number];
  compensatedNorm: [number, number];
  velocityNormPerSec: [number, number];
  speedNormPerSec: number;
  accelNormPerSec2: number;
  motionState: MotionState;
  qPos: number;
  qVel: number;
  r: number;
  qPosX: number;
  qPosY: number;
  qVelX: number;
  qVelY: number;
  rX: number;
  rY: number;
  tauSec: number;
  lagGainX: number;
  lagGainY: number;
  leadNorm: [number, number];
  dtSec: number;
}

const EPS = 1e-9;

export const DEFAULT_GAZE_ENHANCEMENT_PARAMS: GazeEnhancementParams = {
  rFixation: 0.00075,
  rPursuit: 0.00075,
  rSaccade: 0.00075,
  qPos: 0.00001,
  qVelFixation: 0.0001,
  qVelPursuit: 0.0001,
  qVelSaccade: 0.0001,
  processNoiseXScale: 1,
  processNoiseYScale: 1.2,
  measurementNoiseXScale: 1,
  measurementNoiseYScale: 0.8,
  fixationEnterSpeed: 0.1,
  fixationExitSpeed: 0.15,
  saccadeEnterSpeed: 0.55,
  saccadeExitSpeed: 0.35,
  saccadeEnterAccel: 2.5,
  saccadeExitAccel: 1.3,
  minStateHoldMs: 100,
  tauFixationSec: 0.01,
  tauPursuitSec: 0.04,
  tauSaccadeSec: 0.04,
  lagGainX: 0.8,
  lagGainY: 1.0,
  maxLeadNorm: 0.5,
  minDtSec: 1 / 240,
  maxDtSec: 1 / 15,
};

export const DEFAULT_GAZE_ENHANCEMENT_TOGGLES: GazeEnhancementToggles = {
  enableKalman: true,
  enableAxisAsymmetry: true,
  enableMotionState: false,
  enableLagCompensation: true,
  enableDtClamp: false,
};

type Matrix = number[][];

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function cloneMatrix(m: Matrix): Matrix {
  return m.map((row) => [...row]);
}

function identity(n: number): Matrix {
  const m: Matrix = Array.from({ length: n }, () => Array(n).fill(0));
  for (let i = 0; i < n; i++) m[i][i] = 1;
  return m;
}

function add(a: Matrix, b: Matrix): Matrix {
  const rows = a.length;
  const cols = a[0].length;
  const out: Matrix = Array.from({ length: rows }, () => Array(cols).fill(0));
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) out[r][c] = a[r][c] + b[r][c];
  }
  return out;
}

function sub(a: Matrix, b: Matrix): Matrix {
  const rows = a.length;
  const cols = a[0].length;
  const out: Matrix = Array.from({ length: rows }, () => Array(cols).fill(0));
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) out[r][c] = a[r][c] - b[r][c];
  }
  return out;
}

function mul(a: Matrix, b: Matrix): Matrix {
  const rows = a.length;
  const cols = b[0].length;
  const inner = b.length;
  const out: Matrix = Array.from({ length: rows }, () => Array(cols).fill(0));
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      let sum = 0;
      for (let k = 0; k < inner; k++) sum += a[r][k] * b[k][c];
      out[r][c] = sum;
    }
  }
  return out;
}

function transpose(a: Matrix): Matrix {
  const rows = a.length;
  const cols = a[0].length;
  const out: Matrix = Array.from({ length: cols }, () => Array(rows).fill(0));
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) out[c][r] = a[r][c];
  }
  return out;
}

function inverse2x2(a: Matrix): Matrix {
  const m00 = a[0][0];
  const m01 = a[0][1];
  const m10 = a[1][0];
  const m11 = a[1][1];
  const det = m00 * m11 - m01 * m10;
  if (Math.abs(det) < EPS) {
    const fallback = 1 / (EPS + Math.abs(det));
    return [
      [m11 * fallback, -m01 * fallback],
      [-m10 * fallback, m00 * fallback],
    ];
  }
  const invDet = 1 / det;
  return [
    [m11 * invDet, -m01 * invDet],
    [-m10 * invDet, m00 * invDet],
  ];
}

function sanitizeParams(input: GazeEnhancementParams): GazeEnhancementParams {
  const minDtSec = clamp(input.minDtSec, 1 / 1000, 1 / 10);
  const maxDtSec = clamp(input.maxDtSec, minDtSec, 0.3);
  const fixationEnterSpeed = Math.max(0.001, input.fixationEnterSpeed);
  const fixationExitSpeed = Math.max(fixationEnterSpeed + 1e-4, input.fixationExitSpeed);
  const saccadeExitSpeed = Math.max(fixationExitSpeed + 1e-4, input.saccadeExitSpeed);
  const saccadeEnterSpeed = Math.max(saccadeExitSpeed + 1e-4, input.saccadeEnterSpeed);
  const saccadeExitAccel = Math.max(0.001, input.saccadeExitAccel);
  const saccadeEnterAccel = Math.max(saccadeExitAccel + 1e-4, input.saccadeEnterAccel);

  return {
    ...input,
    minDtSec,
    maxDtSec,
    qPos: Math.max(1e-8, input.qPos),
    qVelFixation: Math.max(1e-8, input.qVelFixation),
    qVelPursuit: Math.max(1e-8, input.qVelPursuit),
    qVelSaccade: Math.max(1e-8, input.qVelSaccade),
    rFixation: Math.max(1e-8, input.rFixation),
    rPursuit: Math.max(1e-8, input.rPursuit),
    rSaccade: Math.max(1e-8, input.rSaccade),
    fixationEnterSpeed,
    fixationExitSpeed,
    saccadeEnterSpeed,
    saccadeExitSpeed,
    saccadeEnterAccel,
    saccadeExitAccel,
    minStateHoldMs: Math.max(0, input.minStateHoldMs),
    tauFixationSec: Math.max(0, input.tauFixationSec),
    tauPursuitSec: Math.max(0, input.tauPursuitSec),
    tauSaccadeSec: Math.max(0, input.tauSaccadeSec),
    maxLeadNorm: Math.max(0, input.maxLeadNorm),
    processNoiseXScale: clamp(input.processNoiseXScale, 0.05, 20),
    processNoiseYScale: clamp(input.processNoiseYScale, 0.05, 20),
    measurementNoiseXScale: clamp(input.measurementNoiseXScale, 0.05, 20),
    measurementNoiseYScale: clamp(input.measurementNoiseYScale, 0.05, 20),
    lagGainX: clamp(input.lagGainX, 0, 3),
    lagGainY: clamp(input.lagGainY, 0, 3),
  };
}

function sanitizeToggles(input: GazeEnhancementToggles): GazeEnhancementToggles {
  return {
    enableKalman: Boolean(input.enableKalman),
    enableAxisAsymmetry: Boolean(input.enableAxisAsymmetry),
    enableMotionState: Boolean(input.enableMotionState),
    enableLagCompensation: Boolean(input.enableLagCompensation),
    enableDtClamp: Boolean(input.enableDtClamp),
  };
}

export class AdaptiveGazeEnhancer {
  private params: GazeEnhancementParams;
  private toggles: GazeEnhancementToggles;
  private x: Matrix = [[0], [0], [0], [0]];
  private P: Matrix = identity(4);
  private lastTimestampMs: number | null = null;
  private initialized = false;
  private motionState: MotionState = 'fixation';
  private motionStateSinceMs = 0;
  private prevSpeed = 0;

  private readonly H: Matrix = [
    [1, 0, 0, 0],
    [0, 1, 0, 0],
  ];

  constructor(
    params: GazeEnhancementParams = DEFAULT_GAZE_ENHANCEMENT_PARAMS,
    toggles: GazeEnhancementToggles = DEFAULT_GAZE_ENHANCEMENT_TOGGLES
  ) {
    this.params = sanitizeParams(params);
    this.toggles = sanitizeToggles(toggles);
  }

  setParams(params: Partial<GazeEnhancementParams>) {
    this.params = sanitizeParams({ ...this.params, ...params });
  }

  setToggles(toggles: Partial<GazeEnhancementToggles>) {
    this.toggles = sanitizeToggles({ ...this.toggles, ...toggles });
  }

  reset(seedNormX?: number, seedNormY?: number, timestampMs?: number) {
    const px = Number.isFinite(seedNormX) ? clamp(Number(seedNormX), -0.5, 0.5) : 0;
    const py = Number.isFinite(seedNormY) ? clamp(Number(seedNormY), -0.5, 0.5) : 0;
    this.x = [[px], [py], [0], [0]];
    this.P = identity(4);
    this.lastTimestampMs = Number.isFinite(timestampMs) ? Number(timestampMs) : null;
    this.initialized = true;
    this.motionState = 'fixation';
    this.motionStateSinceMs = this.lastTimestampMs ?? performance.now();
    this.prevSpeed = 0;
  }

  update(input: GazeEnhancementInput): GazeEnhancementOutput {
    const rawNorm: [number, number] = [clamp(input.rawNormX, -0.5, 0.5), clamp(input.rawNormY, -0.5, 0.5)];
    const ts = Number.isFinite(input.timestampMs) ? input.timestampMs : Date.now();
    const axisEnabled = this.toggles.enableAxisAsymmetry;
    const processXScale = axisEnabled ? this.params.processNoiseXScale : 1;
    const processYScale = axisEnabled ? this.params.processNoiseYScale : 1;
    const measureXScale = axisEnabled ? this.params.measurementNoiseXScale : 1;
    const measureYScale = axisEnabled ? this.params.measurementNoiseYScale : 1;

    if (!this.initialized) {
      this.reset(rawNorm[0], rawNorm[1], ts);
      return {
        rawNorm,
        kalmanNorm: rawNorm,
        compensatedNorm: rawNorm,
        velocityNormPerSec: [0, 0],
        speedNormPerSec: 0,
        accelNormPerSec2: 0,
        motionState: this.toggles.enableMotionState ? this.motionState : 'pursuit',
        qPos: this.params.qPos,
        qVel: this.params.qVelFixation,
        r: this.params.rFixation,
        qPosX: this.params.qPos * processXScale,
        qPosY: this.params.qPos * processYScale,
        qVelX: this.params.qVelFixation * processXScale,
        qVelY: this.params.qVelFixation * processYScale,
        rX: this.params.rFixation * measureXScale,
        rY: this.params.rFixation * measureYScale,
        tauSec: this.toggles.enableLagCompensation ? this.params.tauFixationSec : 0,
        lagGainX: this.toggles.enableLagCompensation ? this.params.lagGainX : 0,
        lagGainY: this.toggles.enableLagCompensation ? this.params.lagGainY : 0,
        leadNorm: [0, 0],
        dtSec: this.params.minDtSec,
      };
    }

    const dtRaw = this.lastTimestampMs == null ? this.params.minDtSec : (ts - this.lastTimestampMs) / 1000;
    const dtSec = this.toggles.enableDtClamp
      ? clamp(dtRaw, this.params.minDtSec, this.params.maxDtSec)
      : Math.max(1 / 1000, dtRaw);
    this.lastTimestampMs = ts;

    const stateForNoise: MotionState = this.toggles.enableMotionState ? this.motionState : 'pursuit';
    const currentNoise = this.getNoiseForState(stateForNoise);
    const qPosX = currentNoise.qPos * processXScale;
    const qPosY = currentNoise.qPos * processYScale;
    const qVelX = currentNoise.qVel * processXScale;
    const qVelY = currentNoise.qVel * processYScale;
    const rX = currentNoise.r * measureXScale;
    const rY = currentNoise.r * measureYScale;

    const prevPosX = this.x[0][0];
    const prevPosY = this.x[1][0];

    if (this.toggles.enableKalman) {
      this.predict(dtSec, qPosX, qPosY, qVelX, qVelY);
      if (input.gazeOpen) this.updateMeasurement(rawNorm[0], rawNorm[1], rX, rY);
    } else {
      this.x[0][0] = rawNorm[0];
      this.x[1][0] = rawNorm[1];
      this.x[2][0] = (rawNorm[0] - prevPosX) / Math.max(dtSec, EPS);
      this.x[3][0] = (rawNorm[1] - prevPosY) / Math.max(dtSec, EPS);
    }

    const kalmanX = this.x[0][0];
    const kalmanY = this.x[1][0];
    const velX = this.x[2][0];
    const velY = this.x[3][0];
    const speed = Math.hypot(velX, velY);
    const accel = Math.abs(speed - this.prevSpeed) / Math.max(dtSec, EPS);
    const nextState = this.toggles.enableMotionState ? this.classifyMotion(speed, accel, ts) : 'pursuit';
    this.motionState = nextState;

    const tauSec = this.toggles.enableLagCompensation ? this.getTau(nextState) : 0;
    const lagGainX = this.toggles.enableLagCompensation ? this.params.lagGainX : 0;
    const lagGainY = this.toggles.enableLagCompensation ? this.params.lagGainY : 0;
    let leadX = velX * tauSec * lagGainX;
    let leadY = velY * tauSec * lagGainY;
    const leadNorm = Math.hypot(leadX, leadY);
    if (leadNorm > this.params.maxLeadNorm && leadNorm > EPS) {
      const scale = this.params.maxLeadNorm / leadNorm;
      leadX *= scale;
      leadY *= scale;
    }

    const compensatedNorm: [number, number] = [clamp(kalmanX + leadX, -0.5, 0.5), clamp(kalmanY + leadY, -0.5, 0.5)];
    this.prevSpeed = speed;

    return {
      rawNorm,
      kalmanNorm: [kalmanX, kalmanY],
      compensatedNorm,
      velocityNormPerSec: [velX, velY],
      speedNormPerSec: speed,
      accelNormPerSec2: accel,
      motionState: nextState,
      qPos: currentNoise.qPos,
      qVel: currentNoise.qVel,
      r: currentNoise.r,
      qPosX,
      qPosY,
      qVelX,
      qVelY,
      rX,
      rY,
      tauSec,
      lagGainX,
      lagGainY,
      leadNorm: [leadX, leadY],
      dtSec,
    };
  }

  private getTau(state: MotionState): number {
    if (state === 'saccade') return this.params.tauSaccadeSec;
    if (state === 'pursuit') return this.params.tauPursuitSec;
    return this.params.tauFixationSec;
  }

  private getNoiseForState(state: MotionState): { qPos: number; qVel: number; r: number } {
    if (state === 'saccade') return { qPos: this.params.qPos, qVel: this.params.qVelSaccade, r: this.params.rSaccade };
    if (state === 'pursuit') return { qPos: this.params.qPos, qVel: this.params.qVelPursuit, r: this.params.rPursuit };
    return { qPos: this.params.qPos, qVel: this.params.qVelFixation, r: this.params.rFixation };
  }

  private classifyMotion(speed: number, accel: number, nowMs: number): MotionState {
    const cur = this.motionState;
    const p = this.params;
    let next: MotionState = cur;
    if (cur === 'fixation') {
      if (speed >= p.saccadeEnterSpeed && accel >= p.saccadeEnterAccel) next = 'saccade';
      else if (speed >= p.fixationExitSpeed) next = 'pursuit';
    } else if (cur === 'pursuit') {
      if (speed >= p.saccadeEnterSpeed && accel >= p.saccadeEnterAccel) next = 'saccade';
      else if (speed <= p.fixationEnterSpeed) next = 'fixation';
    } else {
      if (speed <= p.fixationEnterSpeed) next = 'fixation';
      else if (speed <= p.saccadeExitSpeed || accel <= p.saccadeExitAccel) next = 'pursuit';
    }
    if (next !== cur && nowMs - this.motionStateSinceMs < p.minStateHoldMs) return cur;
    if (next !== cur) this.motionStateSinceMs = nowMs;
    return next;
  }

  private predict(dtSec: number, qPosX: number, qPosY: number, qVelX: number, qVelY: number) {
    const F: Matrix = [[1, 0, dtSec, 0], [0, 1, 0, dtSec], [0, 0, 1, 0], [0, 0, 0, 1]];
    const Q: Matrix = [[qPosX, 0, 0, 0], [0, qPosY, 0, 0], [0, 0, qVelX, 0], [0, 0, 0, qVelY]];
    this.x = mul(F, this.x);
    this.P = add(mul(mul(F, this.P), transpose(F)), Q);
  }

  private updateMeasurement(rawNormX: number, rawNormY: number, rX: number, rY: number) {
    const z: Matrix = [[rawNormX], [rawNormY]];
    const y = sub(z, mul(this.H, this.x));
    const S = add(mul(mul(this.H, this.P), transpose(this.H)), [[rX, 0], [0, rY]]);
    const K = mul(mul(this.P, transpose(this.H)), inverse2x2(S));
    this.x = add(this.x, mul(K, y));

    const KH = mul(K, this.H);
    const I = identity(4);
    this.P = mul(sub(I, KH), this.P);
    this.x[0][0] = clamp(this.x[0][0], -0.5, 0.5);
    this.x[1][0] = clamp(this.x[1][0], -0.5, 0.5);
    if (!Number.isFinite(this.x[2][0])) this.x[2][0] = 0;
    if (!Number.isFinite(this.x[3][0])) this.x[3][0] = 0;
    this.P = cloneMatrix(this.P).map((row, rowIdx) =>
      row.map((v, colIdx) => {
        if (!Number.isFinite(v)) return rowIdx === colIdx ? 1 : 0;
        if (rowIdx === colIdx) return Math.max(v, 1e-10);
        return v;
      })
    );
  }
}
